/**
 * ResetPassword Component
 *
 * Password reset confirmation page.
 * Integrates with @cfleet/api-client useConfirmPasswordReset hook for backend API calls.
 *
 * Flow:
 * 1. Read token from URL params (?token=xxx)
 * 2. User enters new password and confirms it
 * 3. Form submits to POST /auth/password/reset/confirm via useConfirmPasswordReset hook
 * 4. On success, show success message and redirect to login
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useConfirmPasswordReset } from '@cfleet/api-client';
import {
  ArrowRight,
  ArrowLeft,
  Lock,
  ShieldCheck,
  AlertCircle,
  CheckCircle2,
  Loader2,
} from 'lucide-react';

const ResetPassword: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const { mutate: confirmReset, isPending } = useConfirmPasswordReset();

  // Form state
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // Auto-redirect after success
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => {
        navigate('/login');
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [success, navigate]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validate passwords
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    if (!token) {
      setError('Invalid reset token');
      return;
    }

    confirmReset(
      { token, new_password: password },
      {
        onSuccess: () => {
          setSuccess(true);
        },
        onError: (err) => {
          setError(err instanceof Error ? err.message : 'An error occurred');
        },
      }
    );
  };

  const handleBackToSignIn = () => {
    navigate('/login');
  };

  // Clear error when user starts typing
  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (error) setError(null);
    setPassword(e.target.value);
  };

  const handleConfirmPasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (error) setError(null);
    setConfirmPassword(e.target.value);
  };

  // No token provided
  if (!token) {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center p-4 font-sans text-slate-900">
        <div className="w-full max-w-md bg-white rounded-2xl shadow-xl border border-slate-100 p-8 text-center">
          <div className="w-16 h-16 bg-rose-100 text-rose-600 rounded-full flex items-center justify-center mx-auto mb-6">
            <AlertCircle size={32} />
          </div>
          <h2 className="text-xl font-bold text-slate-900 mb-2">Invalid Token</h2>
          <p className="text-slate-500 mb-6">
            No reset token was provided. Please request a new password reset link.
          </p>
          <button onClick={handleBackToSignIn} className="text-cfleet-600 font-bold hover:underline">
            Back to Sign In
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center p-4 font-sans text-slate-900 relative overflow-hidden">
      {/* Background Decor */}
      <div className="absolute top-0 left-0 w-full h-96 bg-gradient-to-b from-cfleet-100/50 to-transparent -z-10"></div>

      <div className="w-full max-w-md bg-white rounded-2xl shadow-xl border border-slate-100 overflow-hidden animate-in fade-in zoom-in-95 duration-500">
        {!success ? (
          <div className="p-8">
            {/* Logo */}
            <div className="flex justify-center mb-8">
              <div className="flex items-center gap-2.5">
                <div className="w-10 h-10 bg-cfleet-600 rounded-lg flex items-center justify-center text-white text-sm font-bold shadow-lg shadow-cfleet-500/30">
                  C
                </div>
                <span className="font-bold text-3xl text-slate-800 tracking-tight">cfleet</span>
              </div>
            </div>

            <div className="mb-8 text-center">
              <h2 className="text-xl font-bold text-slate-900">Set new password</h2>
              <p className="text-sm text-slate-500 mt-2">
                Enter your new password below.
              </p>
            </div>

            {/* Error Message */}
            {error && (
              <div className="mb-6 p-3 bg-rose-50 border border-rose-200 rounded-lg flex items-start gap-2 animate-in fade-in slide-in-from-top-2">
                <AlertCircle size={18} className="text-rose-500 shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-rose-700">{error}</p>
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label
                  htmlFor="password"
                  className="block text-xs font-bold text-slate-700 uppercase mb-1.5 tracking-wide"
                >
                  New Password
                </label>
                <div className="relative group">
                  <div className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-cfleet-500 transition-colors">
                    <Lock size={18} />
                  </div>
                  <input
                    id="password"
                    type="password"
                    required
                    value={password}
                    onChange={handlePasswordChange}
                    disabled={isPending}
                    className="w-full pl-10 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-cfleet-500/20 focus:border-cfleet-500 transition-all placeholder:text-slate-400 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                    placeholder="••••••••"
                  />
                </div>
                <p className="text-[10px] text-slate-400 mt-1.5 px-1">
                  Minimum 8 characters
                </p>
              </div>

              <div>
                <label
                  htmlFor="confirmPassword"
                  className="block text-xs font-bold text-slate-700 uppercase mb-1.5 tracking-wide"
                >
                  Confirm New Password
                </label>
                <div className="relative group">
                  <div className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-cfleet-500 transition-colors">
                    <Lock size={18} />
                  </div>
                  <input
                    id="confirmPassword"
                    type="password"
                    required
                    value={confirmPassword}
                    onChange={handleConfirmPasswordChange}
                    disabled={isPending}
                    className="w-full pl-10 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-cfleet-500/20 focus:border-cfleet-500 transition-all placeholder:text-slate-400 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                    placeholder="••••••••"
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={isPending}
                className="w-full bg-cfleet-600 text-white py-3 rounded-lg text-sm font-bold shadow-lg shadow-cfleet-500/30 hover:bg-cfleet-700 hover:shadow-xl hover:-translate-y-0.5 transition-all flex items-center justify-center gap-2 mt-6 disabled:opacity-70 disabled:cursor-not-allowed disabled:transform-none disabled:hover:shadow-lg"
              >
                {isPending ? (
                  <Loader2 size={20} className="animate-spin" />
                ) : (
                  <>
                    Reset Password <ArrowRight size={18} />
                  </>
                )}
              </button>
            </form>
          </div>
        ) : (
          <div className="p-8 text-center">
            <div className="w-16 h-16 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 size={32} />
            </div>
            <h2 className="text-xl font-bold text-slate-900 mb-2">Password reset successfully!</h2>
            <p className="text-sm text-slate-500 mb-6">
              Your password has been updated. Redirecting you to sign in...
            </p>
            <div className="flex justify-center">
              <div className="w-12 h-1 bg-slate-100 rounded-full overflow-hidden">
                <div className="h-full bg-emerald-500 animate-pulse origin-left"></div>
              </div>
            </div>
          </div>
        )}

        <div className="px-8 py-5 bg-slate-50 border-t border-slate-100 text-center">
          <button
            onClick={handleBackToSignIn}
            className="inline-flex items-center gap-1.5 text-xs text-slate-600 font-bold hover:text-cfleet-600 transition-colors"
          >
            <ArrowLeft size={14} /> Back to Sign In
          </button>
        </div>
      </div>

      {/* Footer Info */}
      <div className="mt-8 text-center space-y-3">
        <div className="flex items-center justify-center gap-2 text-xs text-emerald-600 font-medium bg-emerald-50 px-3 py-1 rounded-full w-fit mx-auto border border-emerald-100">
          <ShieldCheck size={12} /> Secure Connection
        </div>
        <p className="text-xs text-slate-400">© 2025 cfleet. All rights reserved.</p>
      </div>
    </div>
  );
};

export default ResetPassword;
